import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NAV, LOGOUT } from "@/constants/testIds";
import { LayoutDashboard, Users, CalendarClock, Star, ScrollText, Settings, LogOut, Target, Upload, BarChart3, UserSquare, Wallet, ClipboardCheck, Coins, Megaphone, ShoppingBag, Clock, FileArchive } from "lucide-react";
import { cn } from "@/lib/utils";
import NotificationBell from "@/components/NotificationBell";
import Brand from "@/components/Brand";
import MobileBottomNav from "@/components/layout/MobileBottomNav";

const NAV_ITEMS = [
  { to: "/", label: "Ana Sayfa", icon: LayoutDashboard, testId: NAV.dashboard, exact: true },
  { to: "/customers", label: "Müşteriler", icon: Users, testId: NAV.customers },
  { to: "/appointments", label: "Randevular", icon: CalendarClock, testId: NAV.appointments },
  { to: "/artists", label: "Personeller", icon: UserSquare, testId: "nav-artists" },
  { to: "/unassigned", label: "Sahipsiz Kayıtlar", icon: UserSquare, testId: "nav-unassigned", roles: ["admin", "super_admin"] },
  { to: "/payouts", label: "Hakedişler", icon: Wallet, testId: "nav-payouts" },
  { to: "/completion-approvals", label: "Gün Sonu Kontrolü", icon: ClipboardCheck, testId: "nav-completion-approvals", roles: ["admin", "super_admin"] },
  { to: "/cash-register", label: "Kasa Raporu", icon: Coins, testId: "nav-cash-register", roles: ["admin", "super_admin", "manager"] },
  { to: "/urun-satisi", label: "Ürün Satışı", icon: ShoppingBag, testId: "nav-product-sales" },
  { to: "/team-shifts", label: "Ekip Shift", icon: Clock, testId: "nav-team-shifts", roles: ["admin", "super_admin", "manager"] },
  { to: "/contracts/archive", label: "Sözleşme Arşivi", icon: FileArchive, testId: "nav-contracts-archive", roles: ["admin", "super_admin"] },
  { to: "/segments", label: "Segmentler", icon: Target, testId: "nav-segments" },
  { to: "/toplu-sms", label: "Toplu SMS", icon: Megaphone, testId: "nav-bulk-sms", roles: ["admin", "super_admin"] },
  { to: "/reports", label: "Raporlar", icon: BarChart3, testId: "nav-reports" },
  { to: "/satisfactions", label: "Memnuniyet", icon: Star, testId: NAV.satisfactions },
  // Feb 2026 — nav cleanup: Veri Aktar + Aktivite ana navigasyondan gizlendi.
  // Route + backend + RBAC + direct URL erişimi hâlâ aktif; ileride Ayarlar
  // altına admin-only taşımak için nav entry burada `hidden: true` bırakıldı.
  { to: "/import", label: "Veri Aktar", icon: Upload, testId: "nav-import", hidden: true },
  { to: "/audit", label: "Aktivite", icon: ScrollText, testId: NAV.audit, hidden: true },
  { to: "/settings", label: "Ayarlar", icon: Settings, testId: NAV.settings },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-[100dvh] flex bg-[#050505] text-white">
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-white/10 bg-[#0a0a0a] flex-col">
        <div className="px-5 py-6 border-b border-white/10">
          <Brand variant="sidebar" href="/" subtitle="Tattoompark Yönetim" />
        </div>
        <div className="px-5 pt-4 pb-2">
          <div className="text-[9px] uppercase tracking-[0.3em] text-neutral-600 font-semibold">Operasyon</div>
        </div>
        <nav className="flex-1 px-2 pb-4 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.filter((item) => !item.hidden && (!item.roles || item.roles.includes(user?.role))).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              data-testid={item.testId}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-all duration-150",
                "hover:bg-white/[0.04] hover:text-white",
                isActive
                  ? "bg-brand-red/10 text-white border-l-2 border-brand-red pl-[10px]"
                  : "text-neutral-400 border-l-2 border-transparent",
              )}
            >
              <item.icon className="w-4 h-4" strokeWidth={1.75} />
              <span className="font-medium tracking-wide">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[9px] text-neutral-600 uppercase tracking-[0.28em] font-semibold">Oturum</div>
              <div className="text-sm mt-1 text-white truncate">{user?.email}</div>
              {user?.role && (
                <div className="text-[9px] text-brand-red uppercase tracking-widest mt-0.5 font-mono">
                  {user.role.replace("_", " ")}
                </div>
              )}
            </div>
            <NotificationBell />
          </div>
          <button
            data-testid={LOGOUT.button}
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-400 border border-white/10 hover:border-brand-red hover:text-brand-red transition-colors rounded-sm"
          >
            <LogOut className="w-3.5 h-3.5" />
            Çıkış
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <MobileBottomNav />
        <div className="min-h-[100dvh] pb-20 lg:pb-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
